<template>
  <AppShell
    active="opportunities"
    :workspace="workspace"
    title="Opportunity pipeline"
    subtitle="Source, filter, and advance land opportunities through the acquisition workflow."
    max-width-class="max-w-none"
    data-testid="opportunities-page"
  >
    <template #actions>
      <a href="/map" class="hidden h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted sm:inline-flex">
        <MapPinned class="size-4" aria-hidden="true" />
        Map
      </a>
      <button type="button" data-testid="clickup-pipeline-sync" class="inline-flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-60" :disabled="isImporting || !workspace.clickupEnabled" @click="syncFromClickup">
        <LoaderCircle v-if="isImporting" class="size-4 animate-spin" aria-hidden="true" />
        <RefreshCw v-else class="size-4" aria-hidden="true" />
        ClickUp
      </button>
      <button type="button" data-testid="run-due-screening" class="inline-flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="isRunningDueScreening" @click="runDueScreening">
        <LoaderCircle v-if="isRunningDueScreening" class="size-4 animate-spin" aria-hidden="true" />
        <RefreshCw v-else class="size-4" aria-hidden="true" />
        Screening
      </button>
      <button type="button" data-testid="source-opportunity-button" class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover" @click="openForm">
        <Plus class="size-4" aria-hidden="true" />
        Source opportunity
      </button>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-label="Pipeline metrics">
      <article v-for="metric in metrics" :key="metric.key" class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
        <div class="mb-3 h-1 rounded-full" :class="metricToneBar(metric.tone)" />
        <p class="text-sm font-medium text-land-ink-muted">{{ metric.label }}</p>
        <p class="mt-2 text-2xl font-semibold">{{ formatNumber(metric.value) }}</p>
        <p class="mt-3 text-xs text-land-ink-soft">{{ metric.detail }}</p>
      </article>
    </section>

    <section class="mt-4 rounded-md border border-land-border bg-land-surface shadow-sm">
      <div class="grid gap-3 border-b border-land-border-soft p-4 lg:grid-cols-[minmax(0,1fr)_180px_180px_220px]">
        <div class="relative">
          <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-land-ink-soft" aria-hidden="true" />
          <input v-model="searchQuery" data-testid="opportunity-search" type="search" aria-label="Search opportunities" placeholder="Search opportunities, markets, counties" class="h-10 w-full rounded-md border border-land-border py-2 pl-9 pr-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
        </div>
        <select v-model="selectedStage" aria-label="Filter by stage" class="h-10 rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
          <option value="all">All stages</option>
          <option v-for="stage in formOptions.stages" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
        </select>
        <select v-model="selectedPriority" aria-label="Filter by priority" class="h-10 rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
          <option value="all">All priorities</option>
          <option v-for="priority in formOptions.priorities" :key="priority.value" :value="priority.value">{{ priority.label }}</option>
        </select>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-md border px-3 text-sm font-semibold"
            :class="compareMode ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
            @click="toggleCompareMode"
          >
            Compare
          </button>
          <select v-model="selectedReadinessSort" :disabled="!compareMode" aria-label="Compare by readiness dimension" class="h-10 min-w-0 flex-1 rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20 disabled:opacity-50" @change="applyReadinessSort">
            <option v-for="dimension in readinessSortOptions" :key="dimension.value" :value="dimension.value">{{ dimension.label }}</option>
          </select>
        </div>
        <div class="flex flex-wrap gap-2 lg:col-span-4" aria-label="Work filters">
          <button
            v-for="filter in workFilters"
            :key="filter.value"
            type="button"
            class="inline-flex h-8 items-center gap-2 rounded-md border px-2.5 text-xs font-semibold"
            :class="selectedWorkFilter === filter.value ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
            @click="selectedWorkFilter = filter.value"
          >
            {{ filter.label }}
            <span v-if="filter.count !== null" class="rounded bg-white/70 px-1.5 py-0.5 text-[11px]">{{ formatNumber(filter.count) }}</span>
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full min-w-[1560px] divide-y divide-land-border-soft text-left text-sm">
          <thead class="bg-land-surface-muted text-xs font-semibold uppercase text-land-ink-soft">
            <tr>
              <th v-for="column in sortableColumns" :key="column.key" scope="col" class="px-4 py-3" :aria-sort="ariaSort(column.key)">
                <button type="button" class="inline-flex items-center gap-1.5 rounded-sm text-left hover:text-land-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-land-primary/30" @click="toggleSort(column.key)">
                  {{ column.label }}
                  <ChevronUp v-if="sortKey === column.key && sortDirection === 'asc'" class="size-3.5" aria-hidden="true" />
                  <ChevronDown v-else-if="sortKey === column.key && sortDirection === 'desc'" class="size-3.5" aria-hidden="true" />
                  <ArrowUpDown v-else class="size-3.5 opacity-50" aria-hidden="true" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-land-border-soft">
            <tr v-for="opportunity in filteredOpportunities" :key="opportunity.id" class="border-l-4 hover:bg-land-surface-muted" :class="rowStateClasses(opportunity)">
              <td class="px-4 py-3">
                <div class="flex items-start gap-2">
                  <button
                    type="button"
                    class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md border transition disabled:cursor-wait disabled:opacity-60"
                    :class="opportunity.favorited ? 'border-amber-200 bg-amber-50 text-amber-500 hover:bg-amber-100' : 'border-land-border bg-white text-land-ink-soft hover:bg-land-surface-muted hover:text-amber-500'"
                    :aria-label="opportunity.favorited ? `Remove ${opportunity.name} from favorites` : `Add ${opportunity.name} to favorites`"
                    :title="opportunity.favorited ? 'Remove favorite' : 'Add favorite'"
                    :data-testid="`favorite-opportunity-${opportunity.id}`"
                    :disabled="isFavoritingOpportunity(opportunity.id)"
                    @click="toggleFavorite(opportunity)"
                  >
                    <LoaderCircle v-if="isFavoritingOpportunity(opportunity.id)" class="size-4 animate-spin" aria-hidden="true" />
                    <Star v-else class="size-4" :class="opportunity.favorited ? 'fill-current' : ''" aria-hidden="true" />
                  </button>
                  <div class="min-w-0">
                    <a :href="`/opportunities/${opportunity.id}`" class="font-semibold text-land-ink hover:text-land-primary">{{ opportunity.name }}</a>
                    <div class="mt-2 flex flex-wrap items-center gap-1.5">
                      <StateBadge :label="opportunity.priorityLabel" :tone="priorityTone(opportunity.priority)" />
                      <span class="text-xs text-land-ink-soft">{{ opportunity.assignee }}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <StateBadge :label="opportunity.health?.stateLabel || 'Unknown'" :tone="opportunity.health?.tone || 'slate'" />
                <p class="mt-2 line-clamp-2 max-w-48 text-xs leading-5 text-land-ink-soft">{{ healthReasons(opportunity).join(' · ') || opportunity.health?.nextAction }}</p>
              </td>
              <td class="px-4 py-3">
                <StateBadge :label="opportunity.screeningSummary?.statusLabel || 'Not run'" :tone="opportunity.screeningSummary?.statusTone || 'slate'" />
                <p class="mt-2 text-xs leading-5 text-land-ink-soft">{{ screeningSummaryText(opportunity) }}</p>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span class="text-lg font-semibold text-land-ink">{{ readinessComposite(opportunity) }}</span>
                  <StateBadge :label="readinessLabel(opportunity)" :tone="readinessTone(opportunity)" />
                </div>
                <div class="mt-2 grid grid-cols-8 gap-0.5" :title="readinessTooltip(opportunity)">
                  <span
                    v-for="dimension in readinessDimensions"
                    :key="dimension.value"
                    class="h-2 rounded-sm"
                    :class="readinessHeatClass(readinessDimensionScore(opportunity, dimension.value))"
                  />
                </div>
                <p class="mt-1 text-xs text-land-ink-soft">{{ readinessCoverage(opportunity) }}</p>
              </td>
              <td class="px-4 py-3">
                <select :value="opportunity.stage" :disabled="isStageUpdating(opportunity.id)" class="h-9 min-w-40 rounded-md border border-land-border bg-white px-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20 disabled:cursor-wait disabled:opacity-60" :data-testid="`stage-select-${opportunity.id}`" @change="updateStage(opportunity, $event.target.value)">
                  <option v-for="stage in formOptions.stages" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
                </select>
                <p class="mt-1 text-xs text-land-ink-soft">{{ opportunity.stageAgeLabel }} in stage</p>
              </td>
              <td class="px-4 py-3 text-land-ink-muted">{{ opportunity.market }} / {{ opportunity.county }}, {{ opportunity.state }}</td>
              <td class="px-4 py-3">
                <div class="flex min-w-32 items-center gap-2">
                  <ProgressBar class="min-w-36 flex-1" :value="safePercent(opportunity.diligenceProgress)" :tone="progressTone(opportunity)" />
                </div>
                <div class="mt-2 flex flex-wrap gap-1.5">
                  <StateBadge v-if="opportunity.diligenceSummary?.blocked" :label="`${opportunity.diligenceSummary.blocked} blocked`" tone="red" />
                  <StateBadge v-if="opportunity.diligenceSummary?.failed" :label="`${opportunity.diligenceSummary.failed} failed`" tone="red" />
                  <span class="text-xs text-land-ink-soft">{{ formatNumber(opportunity.diligenceSummary?.completed) }}/{{ formatNumber(opportunity.diligenceSummary?.total) }} complete</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge v-if="opportunity.taskSummary?.overdue" :label="`${opportunity.taskSummary.overdue} overdue`" tone="red" />
                  <StateBadge v-if="opportunity.taskSummary?.dueSoon" :label="`${opportunity.taskSummary.dueSoon} due soon`" tone="amber" />
                  <StateBadge :label="`${formatNumber(opportunity.openTasks)} open`" tone="slate" />
                </div>
              </td>
              <td class="px-4 py-3">
                <a v-if="opportunity.clickup?.linked" :href="opportunity.clickup.url" target="_blank" rel="noreferrer" class="inline-flex items-center gap-1 text-sm font-medium text-land-primary hover:text-land-primary-hover">
                  <ExternalLink class="size-3.5" aria-hidden="true" />
                  {{ opportunity.clickup.listName || 'Task' }}
                </a>
                <button v-else type="button" class="inline-flex h-8 items-center gap-1 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="isPushingOpportunity(opportunity.id) || !workspace.clickupEnabled" @click="pushToClickup(opportunity)">
                  <LoaderCircle v-if="isPushingOpportunity(opportunity.id)" class="size-3.5 animate-spin" aria-hidden="true" />
                  <UploadCloud v-else class="size-3.5" aria-hidden="true" />
                  Push
                </button>
                <div class="mt-2">
                  <StateBadge :label="opportunity.clickup?.healthLabel || opportunity.clickup?.statusLabel || 'Not synced'" :tone="opportunity.clickup?.healthTone || 'slate'" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredOpportunities.length === 0" class="border-t border-land-border-soft px-4 py-12 text-center">
        <p class="text-sm font-semibold">No matching opportunities</p>
        <p class="mt-2 text-sm text-land-ink-muted">Adjust filters or source a new opportunity.</p>
      </div>
    </section>

    <div v-if="showForm" class="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 p-4" role="dialog" aria-modal="true" aria-labelledby="opportunity-form-title" @keydown.escape="closeForm">
      <div class="mx-auto flex min-h-full max-w-2xl items-center">
        <form class="w-full rounded-md border border-land-border bg-white shadow-xl" data-testid="opportunity-form" @submit.prevent="submitOpportunity">
          <div class="flex items-start justify-between gap-4 border-b border-land-border-soft p-5">
            <div>
              <h2 id="opportunity-form-title" class="text-xl font-semibold tracking-normal">Source opportunity</h2>
              <p class="mt-1 text-sm text-land-ink-muted">Create an opportunity and optionally add its first parcel coordinate for the map.</p>
            </div>
            <button type="button" class="grid size-9 place-items-center rounded-md text-land-ink-muted hover:bg-land-surface-muted" aria-label="Close form" @click="closeForm">
              <X class="size-5" aria-hidden="true" />
            </button>
          </div>

          <div class="grid gap-4 p-5 sm:grid-cols-2">
            <div v-if="formErrors.length > 0" class="sm:col-span-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              <p v-for="error in formErrors" :key="error">{{ error }}</p>
            </div>

            <label class="sm:col-span-2 text-sm font-medium">Opportunity name
              <input v-model.trim="form.name" data-testid="opportunity-name" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="sm:col-span-2 text-sm font-medium">Internal project identifier
              <input v-model.trim="form.internal_identifier" data-testid="opportunity-internal-identifier" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Market
              <input v-model.trim="form.market" data-testid="opportunity-market" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">County
              <input v-model.trim="form.county" data-testid="opportunity-county" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">State
              <input v-model.trim="form.state" data-testid="opportunity-state" required maxlength="2" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm uppercase focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Total acreage estimate
              <input v-model="form.acreage_estimate" data-testid="opportunity-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Stage
              <select v-model="form.stage" data-testid="opportunity-stage" class="mt-2 h-10 w-full rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                <option v-for="stage in formOptions.stages" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
              </select>
            </label>
            <label class="text-sm font-medium">Priority
              <select v-model="form.priority" data-testid="opportunity-priority" class="mt-2 h-10 w-full rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                <option v-for="priority in formOptions.priorities" :key="priority.value" :value="priority.value">{{ priority.label }}</option>
              </select>
            </label>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Initial intake</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">CRM / ClickUp fields
                  <input v-model.trim="form.crm_fields_status" data-testid="opportunity-crm-fields-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Site control status
                  <input v-model.trim="form.site_control_status" data-testid="opportunity-site-control-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Project sponsor
                  <input v-model.trim="form.project_sponsor" data-testid="opportunity-project-sponsor" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Project originator
                  <input v-model.trim="form.project_originator" data-testid="opportunity-project-originator" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Landowner(s)
                  <textarea v-model.trim="form.landowner_summary" data-testid="opportunity-landowner-summary" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Asking price / economics
                  <textarea v-model.trim="form.economics_summary" data-testid="opportunity-economics-summary" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Confidentiality requirements
                  <textarea v-model.trim="form.confidentiality_requirements" data-testid="opportunity-confidentiality-requirements" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Available documents
                  <textarea v-model.trim="form.available_documents_summary" data-testid="opportunity-available-documents" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="text-sm font-medium">Data room status
                  <input v-model.trim="form.data_room_status" data-testid="opportunity-data-room-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Data room URL
                  <input v-model.trim="form.data_room_url" data-testid="opportunity-data-room-url" type="url" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Internal project summary</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-3">
                <label class="sm:col-span-3 text-sm font-medium">Executive summary
                  <textarea v-model.trim="form.summary" data-testid="opportunity-summary" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Strategic rationale
                  <textarea v-model.trim="form.strategic_rationale" data-testid="opportunity-strategic-rationale" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="text-sm font-medium">Immediate MW
                  <input v-model="form.immediate_mw_opportunity" data-testid="opportunity-immediate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Intermediate MW
                  <input v-model="form.intermediate_mw_opportunity" data-testid="opportunity-intermediate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Ultimate MW
                  <input v-model="form.ultimate_mw_opportunity" data-testid="opportunity-ultimate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Utility territory
                  <input v-model.trim="form.utility_territory" data-testid="opportunity-utility-territory" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Competitive positioning
                  <textarea v-model.trim="form.competitive_positioning" data-testid="opportunity-competitive-positioning" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Risks / blockers identified
                  <textarea v-model.trim="form.risks_blockers_identified" data-testid="opportunity-risks-blockers" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Recommended next steps
                  <textarea v-model.trim="form.recommended_next_steps" data-testid="opportunity-recommended-next-steps" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Site information</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">Ownership status
                  <input v-model.trim="form.ownership_status" data-testid="opportunity-ownership-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Current zoning classification
                  <input v-model.trim="form.zoning_classification" data-testid="opportunity-zoning-classification" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Estimated usable acreage
                  <input v-model="form.usable_acreage_estimate" data-testid="opportunity-usable-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Estimated pad acreage
                  <input v-model="form.pad_acreage_estimate" data-testid="opportunity-pad-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Expansion acreage
                  <input v-model="form.expansion_acreage_estimate" data-testid="opportunity-expansion-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Primary parcel location</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">Latitude
                  <input v-model="form.latitude" data-testid="opportunity-latitude" step="0.000001" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Longitude
                  <input v-model="form.longitude" data-testid="opportunity-longitude" step="0.000001" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Parcel/APN
                  <input v-model.trim="form.apn" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
              </div>
            </div>

          </div>

          <div class="flex flex-col-reverse gap-2 border-t border-land-border-soft p-5 sm:flex-row sm:justify-end">
            <button type="button" class="h-10 rounded-md border border-land-border bg-white px-4 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="closeForm">Cancel</button>
            <button type="submit" data-testid="submit-opportunity" class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-land-primary px-4 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-60" :disabled="isSubmitting">
              <LoaderCircle v-if="isSubmitting" class="size-4 animate-spin" aria-hidden="true" />
              <Save v-else class="size-4" aria-hidden="true" />
              Create opportunity
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppShell>
</template>

<script setup>
import { ArrowUpDown, ChevronDown, ChevronUp, ExternalLink, LoaderCircle, MapPinned, Plus, RefreshCw, Save, Search, Star, UploadCloud, X } from '@lucide/vue'
import { computed, ref } from 'vue'
import { compactObject, normalizeErrors, requestJson } from '@/api/client'
import AppShell from '@/components/shared/app_shell.vue'
import ProgressBar from '@/components/shared/progress_bar.vue'
import StateBadge from '@/components/shared/state_badge.vue'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const dashboard = ref(props.payload)
const searchQuery = ref('')
const selectedStage = ref('all')
const selectedPriority = ref('all')
const selectedWorkFilter = ref('all')
const sortKey = ref('updatedAt')
const sortDirection = ref('desc')
const compareMode = ref(false)
const selectedReadinessSort = ref('composite')
const showForm = ref(false)
const isSubmitting = ref(false)
const isImporting = ref(false)
const isRunningDueScreening = ref(false)
const formErrors = ref([])
const errors = ref([])
const notice = ref('')
const updatingStageIds = ref([])
const pushingOpportunityIds = ref([])
const favoritingOpportunityIds = ref([])

const defaultForm = () => ({
  name: '',
  market: workspace.value.marketFocus === 'All markets' ? '' : workspace.value.marketFocus,
  county: '',
  state: 'TX',
  stage: 'lead',
  priority: 'normal',
  internal_identifier: '',
  acreage_estimate: '',
  crm_fields_status: '',
  project_sponsor: '',
  project_originator: '',
  landowner_summary: '',
  site_control_status: '',
  economics_summary: '',
  confidentiality_requirements: '',
  available_documents_summary: '',
  data_room_url: '',
  data_room_status: '',
  strategic_rationale: '',
  immediate_mw_opportunity: '',
  intermediate_mw_opportunity: '',
  ultimate_mw_opportunity: '',
  utility_territory: '',
  competitive_positioning: '',
  risks_blockers_identified: '',
  recommended_next_steps: '',
  ownership_status: '',
  zoning_classification: '',
  usable_acreage_estimate: '',
  pad_acreage_estimate: '',
  expansion_acreage_estimate: '',
  latitude: '',
  longitude: '',
  apn: '',
  summary: ''
})

const workspace = computed(() => dashboard.value.workspace || {})
const formOptions = computed(() => dashboard.value.formOptions || { stages: [], priorities: [] })
const metrics = computed(() => dashboard.value.metrics || [])
const opportunities = computed(() => dashboard.value.opportunities || [])
const sortableColumns = [
  { key: 'name', label: 'Opportunity' },
  { key: 'health', label: 'Health' },
  { key: 'screening', label: 'Screening' },
  { key: 'readiness', label: 'Readiness' },
  { key: 'stage', label: 'Stage' },
  { key: 'market', label: 'Market' },
  { key: 'diligenceProgress', label: 'Diligence' },
  { key: 'taskRisk', label: 'Work' },
  { key: 'clickup', label: 'ClickUp' }
]
const readinessDimensions = [
  { value: 'power', label: 'Power' },
  { value: 'gas', label: 'Gas' },
  { value: 'water', label: 'Water' },
  { value: 'fiber', label: 'Fiber' },
  { value: 'transport', label: 'Transport' },
  { value: 'hazards', label: 'Hazards' },
  { value: 'contamination', label: 'Contamination' },
  { value: 'market', label: 'Market' }
]
const readinessSortOptions = [
  { value: 'composite', label: 'Overall readiness' },
  ...readinessDimensions
]

const workFilters = computed(() => [
  { value: 'all', label: 'All', count: opportunities.value.length },
  { value: 'needs_action', label: 'Needs action', count: opportunities.value.filter(needsAction).length },
  { value: 'blocked', label: 'Blocked', count: opportunities.value.filter((opportunity) => opportunity.health?.state === 'blocked').length },
  { value: 'favorites', label: 'Favorites', count: opportunities.value.filter((opportunity) => opportunity.favorited).length },
  { value: 'screening_review', label: 'Screening review', count: opportunities.value.filter(needsScreeningReview).length },
  { value: 'overdue', label: 'Overdue', count: opportunities.value.filter((opportunity) => Number(opportunity.taskSummary?.overdue || 0) > 0 || Number(opportunity.diligenceSummary?.overdue || 0) > 0).length },
  { value: 'due_soon', label: 'Due soon', count: opportunities.value.filter((opportunity) => Number(opportunity.taskSummary?.dueSoon || 0) > 0 || Number(opportunity.diligenceSummary?.dueSoon || 0) > 0).length },
  { value: 'unmapped', label: 'Unmapped', count: opportunities.value.filter((opportunity) => !opportunity.parcelSummary?.mapped).length },
  { value: 'stale_sync', label: 'Stale sync', count: opportunities.value.filter((opportunity) => opportunity.clickup?.health?.state === 'stale' || opportunity.clickup?.health?.state === 'failed').length }
])

const form = ref(defaultForm())

const filteredOpportunities = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  const filtered = opportunities.value.filter((opportunity) => {
    const matchesStage = selectedStage.value === 'all' || opportunity.stage === selectedStage.value
    const matchesPriority = selectedPriority.value === 'all' || opportunity.priority === selectedPriority.value
    const matchesWork = matchesWorkFilter(opportunity)
    const text = [opportunity.name, opportunity.market, opportunity.county, opportunity.state, opportunity.assignee].join(' ').toLowerCase()

    return matchesStage && matchesPriority && matchesWork && (!query || text.includes(query))
  })

  return [...filtered].sort((left, right) => compareOpportunities(left, right, sortKey.value, sortDirection.value))
})

const compareOpportunities = (left, right, key, direction) => {
  const leftValue = sortableValue(left, key)
  const rightValue = sortableValue(right, key)
  const modifier = direction === 'asc' ? 1 : -1

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return (leftValue - rightValue) * modifier
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' }) * modifier
}

const sortableValue = (opportunity, key) => ({
  name: opportunity.name,
  health: Number(opportunity.health?.score || 0),
  screening: screeningRiskScore(opportunity),
  readiness: readinessScoreForSort(opportunity, 'composite'),
  clickup: opportunity.clickup?.listName || opportunity.clickup?.statusLabel || 'Not synced',
  stage: opportunity.stageLabel,
  market: `${opportunity.market} ${opportunity.county} ${opportunity.state}`,
  acreage: Number(opportunity.acreage || 0),
  diligenceProgress: Number(opportunity.diligenceProgress || 0),
  openTasks: Number(opportunity.openTasks || 0),
  taskRisk: Number(opportunity.taskSummary?.overdue || 0) * 100 + Number(opportunity.taskSummary?.dueSoon || 0) * 10 + Number(opportunity.openTasks || 0),
  updatedAt: Date.parse(opportunity.updatedAt || '') || 0
})[key] ?? (key.startsWith('readiness:') ? readinessScoreForSort(opportunity, key.split(':')[1]) : null)

const matchesWorkFilter = (opportunity) => ({
  all: true,
  needs_action: needsAction(opportunity),
  blocked: opportunity.health?.state === 'blocked',
  favorites: Boolean(opportunity.favorited),
  screening_review: needsScreeningReview(opportunity),
  overdue: Number(opportunity.taskSummary?.overdue || 0) > 0 || Number(opportunity.diligenceSummary?.overdue || 0) > 0,
  due_soon: Number(opportunity.taskSummary?.dueSoon || 0) > 0 || Number(opportunity.diligenceSummary?.dueSoon || 0) > 0,
  unmapped: !opportunity.parcelSummary?.mapped,
  stale_sync: opportunity.clickup?.health?.state === 'stale' || opportunity.clickup?.health?.state === 'failed'
})[selectedWorkFilter.value] ?? true

const needsAction = (opportunity) => ['attention', 'blocked', 'overdue', 'stale', 'incomplete'].includes(opportunity.health?.state) || needsScreeningReview(opportunity)
const needsScreeningReview = (opportunity) => ['failed', 'blocked', 'review_required', 'incomplete', 'partial', 'not_run'].includes(opportunity.screeningSummary?.status)

const toggleSort = (key) => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDirection.value = 'asc'
  }
}

const toggleCompareMode = () => {
  compareMode.value = !compareMode.value
  if (compareMode.value) applyReadinessSort()
}

const applyReadinessSort = () => {
  if (!compareMode.value) return

  sortKey.value = selectedReadinessSort.value === 'composite' ? 'readiness' : `readiness:${selectedReadinessSort.value}`
  sortDirection.value = 'desc'
}

const ariaSort = (key) => {
  if (sortKey.value !== key) return 'none'

  return sortDirection.value === 'asc' ? 'ascending' : 'descending'
}

const openForm = () => {
  form.value = defaultForm()
  formErrors.value = []
  showForm.value = true
}

const closeForm = () => {
  if (isSubmitting.value) return

  showForm.value = false
}

const submitOpportunity = async () => {
  isSubmitting.value = true
  formErrors.value = []
  errors.value = []

  const primaryParcel = compactObject({
    latitude: form.value.latitude,
    longitude: form.value.longitude,
    apn: form.value.apn
  })
  const opportunity = compactObject({
    name: form.value.name,
    market: form.value.market,
    county: form.value.county,
    state: form.value.state,
    stage: form.value.stage,
    priority: form.value.priority,
    internal_identifier: form.value.internal_identifier,
    acreage_estimate: form.value.acreage_estimate,
    crm_fields_status: form.value.crm_fields_status,
    project_sponsor: form.value.project_sponsor,
    project_originator: form.value.project_originator,
    landowner_summary: form.value.landowner_summary,
    site_control_status: form.value.site_control_status,
    economics_summary: form.value.economics_summary,
    confidentiality_requirements: form.value.confidentiality_requirements,
    available_documents_summary: form.value.available_documents_summary,
    data_room_url: form.value.data_room_url,
    data_room_status: form.value.data_room_status,
    strategic_rationale: form.value.strategic_rationale,
    immediate_mw_opportunity: form.value.immediate_mw_opportunity,
    intermediate_mw_opportunity: form.value.intermediate_mw_opportunity,
    ultimate_mw_opportunity: form.value.ultimate_mw_opportunity,
    utility_territory: form.value.utility_territory,
    competitive_positioning: form.value.competitive_positioning,
    risks_blockers_identified: form.value.risks_blockers_identified,
    recommended_next_steps: form.value.recommended_next_steps,
    ownership_status: form.value.ownership_status,
    zoning_classification: form.value.zoning_classification,
    usable_acreage_estimate: form.value.usable_acreage_estimate,
    pad_acreage_estimate: form.value.pad_acreage_estimate,
    expansion_acreage_estimate: form.value.expansion_acreage_estimate,
    summary: form.value.summary,
    primary_parcel: Object.keys(primaryParcel).length > 0 ? primaryParcel : undefined
  })

  try {
    const data = await requestJson('/opportunities', {
      method: 'POST',
      body: JSON.stringify({ opportunity })
    })

    dashboard.value = data.dashboard
    notice.value = `${data.opportunity.name} created.`
    searchQuery.value = data.opportunity.name
    selectedStage.value = 'all'
    selectedPriority.value = 'all'
    showForm.value = false
  } catch (error) {
    formErrors.value = normalizeErrors(error)
  } finally {
    isSubmitting.value = false
  }
}

const updateStage = async (opportunity, stage) => {
  if (opportunity.stage === stage) return

  updatingStageIds.value = [...updatingStageIds.value, opportunity.id]
  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage })
    })

    dashboard.value = data.dashboard
    notice.value = `${data.opportunity.name} moved to ${data.opportunity.stageLabel}.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    updatingStageIds.value = updatingStageIds.value.filter((id) => id !== opportunity.id)
  }
}

const syncFromClickup = async () => {
  isImporting.value = true
  errors.value = []

  try {
    const data = await requestJson('/clickup/pipeline_sync', {
      method: 'POST'
    })
    const counts = data.counts || {}

    dashboard.value = data.dashboard
    notice.value = `ClickUp synced: ${formatNumber(counts.imported)} imported, ${formatNumber(counts.updated)} updated.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isImporting.value = false
  }
}

const runDueScreening = async () => {
  isRunningDueScreening.value = true
  errors.value = []

  try {
    const data = await requestJson('/qualification_screenings/run_due', {
      method: 'POST',
      body: JSON.stringify({
        screening: {
          max_opportunities: 8,
          apply_findings: true
        }
      })
    })
    const automation = data.automation || {}

    dashboard.value = data.dashboard
    notice.value = automation.opportunitiesRun > 0
      ? `Screening automation ran ${formatNumber(automation.keysRun)} checks across ${formatNumber(automation.opportunitiesRun)} opportunities.`
      : 'No due screening checks found.'
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isRunningDueScreening.value = false
  }
}

const pushToClickup = async (opportunity) => {
  pushingOpportunityIds.value = [...pushingOpportunityIds.value, opportunity.id]
  errors.value = []

  try {
    const data = await requestJson(`/clickup/opportunity_syncs/${opportunity.id}`, {
      method: 'POST'
    })

    replaceOpportunity(data.opportunity)
    notice.value = `${data.opportunity.name} synced to ClickUp.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    pushingOpportunityIds.value = pushingOpportunityIds.value.filter((id) => id !== opportunity.id)
  }
}

const toggleFavorite = async (opportunity) => {
  favoritingOpportunityIds.value = [...favoritingOpportunityIds.value, opportunity.id]
  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.id}/favorite`, {
      method: opportunity.favorited ? 'DELETE' : 'POST'
    })

    replaceOpportunity(data.opportunity)
    updateFavoriteMetric(data.favoriteCount)
    notice.value = data.opportunity.favorited
      ? `${data.opportunity.name} added to favorites.`
      : `${data.opportunity.name} removed from favorites.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    favoritingOpportunityIds.value = favoritingOpportunityIds.value.filter((id) => id !== opportunity.id)
  }
}

const replaceOpportunity = (opportunity) => {
  dashboard.value = {
    ...dashboard.value,
    opportunities: opportunities.value.map((existingOpportunity) => (
      existingOpportunity.id === opportunity.id ? opportunity : existingOpportunity
    ))
  }
}

const updateFavoriteMetric = (favoriteCount) => {
  dashboard.value = {
    ...dashboard.value,
    metrics: metrics.value.map((metric) => (
      metric.key === 'favorites' ? { ...metric, value: favoriteCount } : metric
    ))
  }
}

const isStageUpdating = (id) => updatingStageIds.value.includes(id)
const isPushingOpportunity = (id) => pushingOpportunityIds.value.includes(id)
const isFavoritingOpportunity = (id) => favoritingOpportunityIds.value.includes(id)
const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const formatShortDate = (value) => {
  if (!value) return null

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  }).format(new Date(value))
}
const safePercent = (value) => Math.min(100, Math.max(0, Number(value || 0)))
const healthReasons = (opportunity) => opportunity.health?.reasons || []
const screeningSummaryText = (opportunity) => {
  const summary = opportunity.screeningSummary || {}
  const progress = `${formatNumber(summary.run)}/${formatNumber(summary.total)} checks`
  const date = formatShortDate(summary.lastRunAt)

  return date ? `${progress} · ${date}` : progress
}
const screeningRiskScore = (opportunity) => {
  const summary = opportunity.screeningSummary || {}
  const base = {
    failed: 600,
    blocked: 500,
    review_required: 400,
    incomplete: 300,
    partial: 200,
    not_run: 100,
    cleared: 0
  }[summary.status] ?? 100

  return base + Number(summary.reviewRequired || 0) * 10 + Number(summary.blocked || 0) * 20 + Number(summary.failed || 0) * 30
}
const readinessScoreForSort = (opportunity, dimension) => {
  if (dimension === 'composite') return Number(opportunity.readiness?.composite ?? -1)

  return Number(opportunity.readiness?.subScores?.[dimension]?.score ?? -1)
}
const readinessComposite = (opportunity) => {
  const score = opportunity.readiness?.composite
  return Number.isFinite(Number(score)) ? Math.round(Number(score)) : '—'
}
const readinessLabel = (opportunity) => {
  const score = Number(opportunity.readiness?.composite)
  if (!Number.isFinite(score)) return 'Not scored'
  if (score >= 80) return 'Strong'
  if (score >= 65) return 'Promising'
  if (score >= 45) return 'Mixed'

  return 'Weak'
}
const readinessTone = (opportunity) => {
  const score = Number(opportunity.readiness?.composite)
  if (!Number.isFinite(score)) return 'slate'
  if (score >= 80) return 'green'
  if (score >= 65) return 'blue'
  if (score >= 45) return 'amber'

  return 'red'
}
const readinessDimensionScore = (opportunity, dimension) => opportunity.readiness?.subScores?.[dimension]?.score
const readinessHeatClass = (score) => {
  const value = Number(score)
  if (!Number.isFinite(value)) return 'bg-slate-200'
  if (value >= 80) return 'bg-emerald-500'
  if (value >= 65) return 'bg-sky-500'
  if (value >= 45) return 'bg-amber-400'

  return 'bg-red-500'
}
const readinessCoverage = (opportunity) => {
  const coverage = Number(opportunity.readiness?.coverage)
  const confidence = Number(opportunity.readiness?.confidence)
  const parts = []
  if (Number.isFinite(coverage)) parts.push(`${Math.round(coverage * 100)}% coverage`)
  if (Number.isFinite(confidence)) parts.push(`${Math.round(confidence)} confidence`)

  return parts.join(' · ') || 'Run screening to score'
}
const readinessTooltip = (opportunity) => readinessDimensions
  .map((dimension) => `${dimension.label}: ${readinessDimensionScore(opportunity, dimension.value) ?? '—'}`)
  .join(' · ')
const progressTone = (opportunity) => {
  if (Number(opportunity.diligenceSummary?.blocked || 0) > 0 || Number(opportunity.diligenceSummary?.failed || 0) > 0) return 'red'
  if (Number(opportunity.diligenceSummary?.overdue || 0) > 0 || Number(opportunity.diligenceSummary?.dueSoon || 0) > 0) return 'amber'
  if (safePercent(opportunity.diligenceProgress) === 100) return 'green'

  return 'blue'
}
const priorityTone = (priority) => ({
  low: 'slate',
  normal: 'blue',
  high: 'amber',
  urgent: 'red'
})[priority] || 'slate'
const rowStateClasses = (opportunity) => ({
  red: 'border-l-red-500',
  amber: 'border-l-amber-500',
  green: 'border-l-emerald-500',
  blue: 'border-l-blue-500',
  violet: 'border-l-violet-500',
  slate: 'border-l-transparent'
})[opportunity.health?.tone] || 'border-l-transparent'
const metricToneBar = (tone) => ({
  primary: 'bg-land-primary',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  green: 'bg-emerald-500',
  slate: 'bg-slate-300'
})[tone] || 'bg-land-primary'
</script>
